// Minha Conta — dados do usuário (nome, apelido, telefone) + preferências
// default de lembrete (canais email/sms/whatsapp/push × tempo antes).
//
// Senha e biometria virão em página separada na próxima fase (precisa Bench
// do legado pra replicar o WebAuthn).

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Bell,
  Brain,
  Check,
  Copy,
  GraduationCap,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  Plus,
  Puzzle,
  Smartphone,
  SpellCheck,
  Sparkles,
  Trash2,
  UserCircle
} from "lucide-react";
import {
  api,
  ApiError,
  type AccountInfo,
  type ApiTokenRow,
  type ReminderChannel,
  type ReminderPref
} from "./api";
import { BiometriaSection } from "./BiometriaSection";
import { PhoneField } from "./PhoneField";
import { CreditsWidget } from "./CreditsWidget";
import { WebhooksSection } from "./WebhooksSection";
import { studentUiVisibility } from "./App";
import { AiPrefsSection } from "./AiPrefsSection";
import { openAlert } from "./ConfirmPromptModal";
import { getDisplayName, getInitials } from "@app/ui/lib/display-name";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { LegalLinks } from "@app/ui";

const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const account = signal<AccountInfo | null>(null);
const prefs = signal<ReminderPref[]>([]);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [a, p] = await Promise.all([api.getAccount(), api.listReminderPrefs()]);
    account.value = a.user;
    prefs.value = p.prefs;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e, {
      fallback: "Não foi possível carregar seus dados agora."
    });
    phase.value = "error";
  }
}

export function MinhaContaView() {
  useEffect(() => {
    void load();
  }, []);

  return (
    <div class="space-y-5">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <UserCircle className="size-6 text-primary" />
          Minha Conta
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Seus dados de contato e preferências de lembrete.
        </p>
      </header>

      {phase.value === "loading" && (
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      )}

      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p class="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            {errMsg.value}
          </p>
        </div>
      )}

      {phase.value === "ready" && account.value && (
        <>
          <PerfilSection acc={account.value} onSaved={load} />
          <PerfilEstendidoSection acc={account.value} onSaved={load} />
          <AiPrefsSection />
          <BiometriaSection />
          <AcessoAplicativosSection />
          {studentUiVisibility.value.webhooks && <WebhooksSection />}
          <LembretesSection prefs={prefs.value} hasPhone={!!account.value.phone} onChanged={load} />
          <CreditsWidget userEmail={account.value.email ?? null} />
          <section class="rounded-md border border-border bg-card/60 p-5">
            <h2 class="mb-1 text-sm font-bold">Documentos legais</h2>
            <p class="mb-3 text-xs text-muted-foreground">
              Consulte os termos e políticas vigentes a qualquer momento.
            </p>
            <LegalLinks />
          </section>
          <EncerrarContaSection />
        </>
      )}
    </div>
  );
}

function PerfilSection({
  acc,
  onSaved
}: {
  acc: AccountInfo;
  onSaved: () => void;
}) {
  const [name, setName] = useState(acc.name);
  const [socialName, setSocialName] = useState(acc.socialName ?? "");
  const [initialsOverride, setInitialsOverride] = useState(
    acc.initialsOverride ?? ""
  );
  const [apelido, setApelido] = useState(acc.apelido ?? "");
  const [phone, setPhone] = useState(acc.phone ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState(false);

  // Preview ao vivo das iniciais — usa o helper compartilhado pra garantir
  // que o auto-gerado bate com o que aparece nos avatares de chat/perfil.
  const previewUser = {
    name: name.trim() || acc.name,
    socialName: socialName.trim() || null,
    initialsOverride: initialsOverride.trim() || null
  };
  const effectiveName = getDisplayName(previewUser);
  const effectiveInitials = getInitials(previewUser);

  async function save() {
    setErr(null);
    setOkMsg(false);
    if (!name.trim()) return setErr("Nome obrigatório.");
    const overTrim = initialsOverride.trim();
    if (overTrim && overTrim.length > 4) {
      return setErr("Iniciais aceitam no máximo 4 caracteres.");
    }
    setSaving(true);
    try {
      await api.patchAccount({
        name: name.trim(),
        socialName: socialName.trim() || null,
        initialsOverride: overTrim ? overTrim.toUpperCase() : null,
        apelido: apelido.trim() || null,
        phone: phone.trim() || null
      });
      setOkMsg(true);
      setTimeout(() => setOkMsg(false), 2000);
      onSaved();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section class="rounded-md border border-border bg-card/60 p-5">
      <h2 class="mb-3 text-sm font-bold">Perfil</h2>
      <div class="grid gap-3 md:grid-cols-2">
        <Field label="Email">
          <input
            type="email"
            aria-label="Email"
            value={acc.email}
            disabled
            class="w-full rounded-md border border-border bg-muted/30 px-3 py-1.5 text-sm text-muted-foreground"
          />
        </Field>
        <Field label="Nome completo">
          <input
            type="text"
            aria-label="Nome completo"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Apelido (opcional)">
          <input
            type="text"
            value={apelido}
            onInput={(e) => setApelido((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            placeholder="ex: João"
          />
        </Field>
        <PhoneField value={phone} onChange={setPhone} />
      </div>

      {/* Identidade visível (nome social + iniciais) — bloco separado pra
          deixar claro o efeito: tudo que aparece pra OUTROS usuários. */}
      <div class="mt-5 rounded-md border border-violet-500/30 bg-violet-500/5 p-4">
        <h3 class="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
          Identidade pra outros usuários
        </h3>
        <p class="mt-1 text-[11px] text-muted-foreground">
          Como você aparece em chat, recibos de pagamento, perfis e demais
          telas vistas por outras pessoas. Seu nome completo continua nos
          registros internos.
        </p>

        <div class="mt-3 grid gap-3 md:grid-cols-2">
          <Field label="Nome social (opcional)">
            <input
              type="text"
              value={socialName}
              maxLength={200}
              onInput={(e) =>
                setSocialName((e.target as HTMLInputElement).value)
              }
              placeholder="O nome pelo qual você quer ser chamado"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
            <span class="mt-1 block text-[10px] text-muted-foreground">
              Se preenchido, substitui o nome completo em todas as telas
              visíveis pra outros.
            </span>
          </Field>
          <Field label="Iniciais (opcional, máx 4)">
            <input
              type="text"
              value={initialsOverride}
              maxLength={4}
              onInput={(e) =>
                setInitialsOverride(
                  (e.target as HTMLInputElement).value.toUpperCase()
                )
              }
              placeholder="Auto"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm uppercase tracking-wider outline-none focus:border-primary"
            />
            <span class="mt-1 block text-[10px] text-muted-foreground">
              Se vazio, geramos com 1ª e última letra do nome usado acima.
            </span>
          </Field>
        </div>

        {/* Preview visual ao vivo — ajuda o usuário a ver como ficou */}
        <div class="mt-3 flex items-center gap-3 rounded-md border border-dashed border-border bg-background/60 px-3 py-2">
          <div class="grid size-10 shrink-0 place-items-center rounded-full bg-brand-gradient text-sm font-bold uppercase text-white">
            {effectiveInitials}
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold">{effectiveName}</div>
            <div class="text-[10px] text-muted-foreground">
              Como você aparece pra outros
            </div>
          </div>
        </div>
      </div>
      {err && (
        <div class="mt-3 flex items-center gap-1.5 text-[11px] text-destructive">
          <AlertCircle className="size-3.5" />
          {err}
        </div>
      )}
      <div class="mt-4 flex items-center justify-end gap-2">
        {okMsg && (
          <span class="flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
            <Check className="size-3.5" /> Salvo
          </span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {saving && <Loader2 className="size-3.5 animate-spin" />}
          Salvar
        </button>
      </div>
    </section>
  );
}

// =============================================================================
// Perfil estendido (escola, nível, ano, curso, objetivos, bio)
// Persistido em users.prefs.studentProfile — sem migration, merge no PATCH.
// Usado depois pra personalização da IA (memória project_user_profile_extended).
// =============================================================================

type EducationLevel =
  | "ensino_medio"
  | "graduacao"
  | "pos_graduacao"
  | "tecnico"
  | "outro";

const EDUCATION_LEVELS: { value: EducationLevel; label: string }[] = [
  { value: "ensino_medio", label: "Ensino médio" },
  { value: "tecnico", label: "Técnico" },
  { value: "graduacao", label: "Graduação" },
  { value: "pos_graduacao", label: "Pós-graduação" },
  { value: "outro", label: "Outro" }
];

interface StudentProfile {
  school?: string;
  educationLevel?: EducationLevel | "";
  course?: string;
  graduationYear?: number | null;
  studyGoals?: string;
  bio?: string;
}

function readStudentProfile(acc: AccountInfo): StudentProfile {
  const p = (acc as { prefs?: Record<string, unknown> | null }).prefs ?? null;
  if (!p || typeof p !== "object") return {};
  const sp = (p as Record<string, unknown>).studentProfile;
  if (!sp || typeof sp !== "object") return {};
  const o = sp as Record<string, unknown>;
  return {
    school: typeof o.school === "string" ? o.school : "",
    educationLevel:
      typeof o.educationLevel === "string"
        ? (o.educationLevel as StudentProfile["educationLevel"])
        : "",
    course: typeof o.course === "string" ? o.course : "",
    graduationYear:
      typeof o.graduationYear === "number" ? o.graduationYear : null,
    studyGoals: typeof o.studyGoals === "string" ? o.studyGoals : "",
    bio: typeof o.bio === "string" ? o.bio : ""
  };
}

function PerfilEstendidoSection({
  acc,
  onSaved
}: {
  acc: AccountInfo;
  onSaved: () => void;
}) {
  const initial = readStudentProfile(acc);
  const [school, setSchool] = useState(initial.school ?? "");
  const [educationLevel, setEducationLevel] = useState<
    StudentProfile["educationLevel"]
  >(initial.educationLevel ?? "");
  const [course, setCourse] = useState(initial.course ?? "");
  const [graduationYear, setGraduationYear] = useState(
    initial.graduationYear ? String(initial.graduationYear) : ""
  );
  const [studyGoals, setStudyGoals] = useState(initial.studyGoals ?? "");
  const [bio, setBio] = useState(initial.bio ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState(false);

  const currentYear = new Date().getFullYear();

  async function save() {
    setErr(null);
    setOkMsg(false);
    const yr = graduationYear.trim();
    let graduationYearNum: number | null = null;
    if (yr) {
      const n = Number(yr);
      if (!Number.isInteger(n) || n < currentYear - 50 || n > currentYear + 15) {
        setErr(`Ano de formação inválido (${currentYear - 50}–${currentYear + 15}).`);
        return;
      }
      graduationYearNum = n;
    }
    const profile: StudentProfile = {
      school: school.trim().slice(0, 200) || undefined,
      educationLevel: educationLevel || undefined,
      course: course.trim().slice(0, 200) || undefined,
      graduationYear: graduationYearNum,
      studyGoals: studyGoals.trim().slice(0, 800) || undefined,
      bio: bio.trim().slice(0, 500) || undefined
    };
    setSaving(true);
    try {
      await api.patchAccount({ prefs: { studentProfile: profile } });
      setOkMsg(true);
      setTimeout(() => setOkMsg(false), 2000);
      onSaved();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section class="rounded-md border border-border bg-card/60 p-5">
      <header class="mb-3 flex items-center gap-2">
        <GraduationCap className="size-4 text-primary" />
        <h2 class="text-sm font-bold">Perfil estendido</h2>
        <span class="text-[10px] text-muted-foreground">
          (opcional — ajuda a IA a personalizar respostas)
        </span>
      </header>

      <div class="grid gap-3 md:grid-cols-2">
        <Field label="Escola / Universidade">
          <input
            type="text"
            value={school}
            maxLength={200}
            onInput={(e) => setSchool((e.target as HTMLInputElement).value)}
            placeholder="ex: USP, Colégio Etapa"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Nível de ensino">
          <select
            aria-label="Nível de ensino"
            value={educationLevel ?? ""}
            onChange={(e) =>
              setEducationLevel(
                ((e.target as HTMLSelectElement).value as StudentProfile["educationLevel"]) ||
                  ""
              )
            }
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          >
            <option value="">—</option>
            {EDUCATION_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Curso / Área">
          <input
            type="text"
            value={course}
            maxLength={200}
            onInput={(e) => setCourse((e.target as HTMLInputElement).value)}
            placeholder="ex: Medicina, Direito, Engenharia"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Ano de formação (previsto)">
          <input
            type="number"
            value={graduationYear}
            min={currentYear - 50}
            max={currentYear + 15}
            onInput={(e) =>
              setGraduationYear((e.target as HTMLInputElement).value)
            }
            placeholder={String(currentYear + 4)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
      </div>

      <div class="mt-3 space-y-3">
        <Field label="Objetivos de estudo">
          <textarea
            value={studyGoals}
            maxLength={800}
            rows={2}
            onInput={(e) =>
              setStudyGoals((e.target as HTMLTextAreaElement).value)
            }
            placeholder="ex: Passar no ENEM/ENADE, revisar pra prova X, dominar tópicos de cálculo…"
            class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Sobre você (curto)">
          <textarea
            value={bio}
            maxLength={500}
            rows={2}
            onInput={(e) => setBio((e.target as HTMLTextAreaElement).value)}
            placeholder="ex: Estudando à noite depois do trabalho. Gosto de explicações com exemplos."
            class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
      </div>

      {err && (
        <div class="mt-3 flex items-center gap-1.5 text-[11px] text-destructive">
          <AlertCircle className="size-3.5" />
          {err}
        </div>
      )}
      <div class="mt-4 flex items-center justify-end gap-2">
        {okMsg && (
          <span class="flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
            <Check className="size-3.5" /> Salvo
          </span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {saving && <Loader2 className="size-3.5 animate-spin" />}
          Salvar
        </button>
      </div>
    </section>
  );
}

const CHANNEL_META: Record<
  ReminderChannel,
  { label: string; icon: typeof Bell; requiresPhone: boolean; mocked: boolean }
> = {
  email: { label: "Email", icon: Mail, requiresPhone: false, mocked: true },
  sms: { label: "SMS", icon: Smartphone, requiresPhone: true, mocked: true },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, requiresPhone: true, mocked: true },
  push: { label: "Push (browser)", icon: Bell, requiresPhone: false, mocked: true }
};

const PRESETS_MIN = [10, 30, 60, 120, 24 * 60, 48 * 60, 7 * 24 * 60];
function fmtOffset(min: number): string {
  if (min < 60) return `${min} min`;
  if (min < 24 * 60) return `${min / 60} h`;
  return `${min / (24 * 60)} d`;
}

function LembretesSection({
  prefs,
  hasPhone,
  onChanged
}: {
  prefs: ReminderPref[];
  hasPhone: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [channel, setChannel] = useState<ReminderChannel>("email");
  const [offsetMinutes, setOffsetMinutes] = useState<number>(60);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    setErr(null);
    setSaving(true);
    try {
      await api.createReminderPref({ offsetMinutes, channel });
      setAdding(false);
      onChanged();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }
  async function remove(id: string) {
    if (!confirm("Remover este lembrete?")) return;
    try {
      await api.deleteReminderPref(id);
      onChanged();
    } catch (e) {
      void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <section class="rounded-md border border-border bg-card/60 p-5">
      <div class="mb-3 flex items-center justify-between">
        <div>
          <h2 class="text-sm font-bold">Lembretes de aula</h2>
          <p class="mt-0.5 text-[11px] text-muted-foreground">
            Você receberá um aviso antes de cada aula. Os canais estão em modo
            mock — em breve disparam de verdade.
          </p>
        </div>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            class="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20"
          >
            <Plus className="size-3.5" />
            Novo
          </button>
        )}
      </div>

      {adding && (
        <div class="mb-3 rounded-md border border-primary/40 bg-primary/5 p-3">
          <div class="grid gap-3 md:grid-cols-2">
            <Field label="Canal">
              <div class="grid grid-cols-2 gap-1.5">
                {(Object.keys(CHANNEL_META) as ReminderChannel[]).map((c) => {
                  const meta = CHANNEL_META[c];
                  const Icon = meta.icon;
                  const disabled = meta.requiresPhone && !hasPhone;
                  const active = channel === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      disabled={disabled}
                      onClick={() => setChannel(c)}
                      title={disabled ? "Cadastre seu telefone no perfil" : undefined}
                      class={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${
                        active
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-muted-foreground hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                      }`}
                    >
                      <Icon className="size-3.5" />
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Quanto tempo antes?">
              <div class="flex flex-wrap gap-1.5">
                {PRESETS_MIN.map((min) => (
                  <button
                    key={min}
                    type="button"
                    onClick={() => setOffsetMinutes(min)}
                    class={`rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      offsetMinutes === min
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-muted-foreground hover:bg-accent"
                    }`}
                  >
                    {fmtOffset(min)}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          {err && (
            <p class="mt-2 flex items-center gap-1.5 text-[11px] text-destructive">
              <AlertCircle className="size-3.5" />
              {err}
            </p>
          )}
          <div class="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setAdding(false)}
              class="rounded-md border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={add}
              disabled={saving}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
              Adicionar
            </button>
          </div>
        </div>
      )}

      {prefs.length === 0 ? (
        <p class="rounded-md border border-dashed border-border bg-background/40 px-4 py-6 text-center text-xs text-muted-foreground">
          Nenhum lembrete configurado. Adicione um pra ser avisado antes das
          aulas.
        </p>
      ) : (
        <ul class="space-y-1.5">
          {prefs.map((p) => {
            const meta = CHANNEL_META[p.channel];
            const Icon = meta.icon;
            return (
              <li
                key={p.id}
                class="flex items-center gap-3 rounded-md border border-border bg-background/60 px-3 py-2"
              >
                <Icon className="size-4 text-muted-foreground" />
                <div class="min-w-0 flex-1 text-xs">
                  <span class="font-semibold">{meta.label}</span>
                  <span class="mx-1.5 text-muted-foreground">·</span>
                  <span class="text-muted-foreground">
                    {fmtOffset(p.offsetMinutes)} antes
                  </span>
                  {meta.mocked && (
                    <span class="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700 dark:text-amber-400">
                      mock
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  title="Remover"
                  aria-label="Remover lembrete"
                  class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-600"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!hasPhone && (
        <div class="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] text-amber-800 dark:text-amber-300">
          <Phone className="mr-1 inline size-3" />
          Cadastre seu telefone no perfil acima pra habilitar SMS e WhatsApp.
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

// ===== Encerrar conta (zona de perigo) — [[project-account-closure]] =====
function AcessoAplicativosSection() {
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [tokens, setTokens] = useState<ApiTokenRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Token recém-criado, mostrado UMA vez (valor cru).
  const [fresh, setFresh] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    try {
      const r = await api.getApiTokens();
      setAllowed(r.allowed);
      setTokens(r.tokens);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  const active = tokens.find((t) => !t.revokedAt);

  async function generate() {
    setBusy(true);
    setErr(null);
    setFresh(null);
    setCopied(false);
    try {
      const r = await api.createApiToken("Extensão Chrome");
      setFresh(r.token);
      await load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setBusy(true);
    setErr(null);
    try {
      await api.revokeApiToken(id);
      setFresh(null);
      await load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  // Gated: invisível até a conta entrar na allowlist do admin.
  if (loading || !allowed) return null;

  return (
    <section class="rounded-md border border-border bg-card/60 p-5">
      <div class="mb-1 flex items-center gap-2">
        <Puzzle className="size-4 text-violet-600 dark:text-violet-400" />
        <h2 class="text-sm font-bold">Acesso de aplicativos</h2>
      </div>
      <p class="mb-3 text-xs text-muted-foreground">
        Gere um token para conectar a extensão do Chrome ao seu assistente.
        Mantenha-o em segredo — é como uma senha. Você pode ter apenas um token
        ativo por vez; gerar um novo revoga o anterior.
      </p>

      {err && <p class="mb-2 text-xs text-destructive">{err}</p>}

      {fresh && (
        <div class="mb-3 rounded-md border border-violet-400/50 bg-violet-500/5 p-3">
          <p class="mb-2 text-xs font-medium text-violet-700 dark:text-violet-300">
            Copie agora — este valor não será mostrado novamente:
          </p>
          <div class="flex items-center gap-2">
            <code class="min-w-0 flex-1 truncate rounded bg-background px-2 py-1.5 text-xs">
              {fresh}
            </code>
            <button
              type="button"
              onClick={() => void copy()}
              class="inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              {copied ? "Copiado" : "Copiar"}
            </button>
          </div>
        </div>
      )}

      {active ? (
        <div class="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background/60 p-3">
          <div class="min-w-0 text-xs">
            <p class="font-medium">{active.label}</p>
            <p class="text-muted-foreground">
              {active.prefix} · criado em{" "}
              {new Date(active.createdAt).toLocaleDateString("pt-BR")}
              {active.lastUsedAt
                ? ` · último uso ${new Date(active.lastUsedAt).toLocaleDateString("pt-BR")}`
                : " · nunca usado"}
              {active.expiresAt
                ? ` · expira ${new Date(active.expiresAt).toLocaleDateString("pt-BR")}`
                : ""}
            </p>
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              onClick={() => void generate()}
              disabled={busy}
              class="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
            >
              Gerar novo
            </button>
            <button
              type="button"
              onClick={() => void revoke(active.id)}
              disabled={busy}
              class="rounded-md border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              Revogar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => void generate()}
          disabled={busy}
          class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
          Gerar token da extensão
        </button>
      )}
    </section>
  );
}

function EncerrarContaSection() {
  const [open, setOpen] = useState<{ id: string; requestedAt: string } | null>(null);
  const [confirmPhrase, setConfirmPhrase] = useState("EXCLUIR MINHA CONTA");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      const r = await api.getAccountClosure();
      setOpen(r.request ? { id: r.request.id, requestedAt: r.request.requestedAt } : null);
      setConfirmPhrase(r.confirmPhrase || "EXCLUIR MINHA CONTA");
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function submit() {
    setErr(null);
    if (typed.trim().toUpperCase() !== confirmPhrase) {
      setErr('Digite exatamente "' + confirmPhrase + '".');
      return;
    }
    setBusy(true);
    try {
      const r = await api.requestAccountClosure(typed.trim(), reason.trim() || undefined);
      setOpen({ id: r.request.id, requestedAt: r.request.requestedAt });
      setShowForm(false);
      setTyped("");
      setReason("");
      setMsg("Solicitação registrada. Processaremos em até 30 dias.");
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    setBusy(true);
    setErr(null);
    try {
      await api.cancelAccountClosure();
      setOpen(null);
      setMsg("Solicitação cancelada.");
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return null;

  return (
    <section class="rounded-md border border-destructive/40 bg-destructive/5 p-5">
      <div class="mb-1 flex items-center gap-2">
        <AlertCircle className="size-4 text-destructive" />
        <h2 class="text-sm font-bold text-destructive">Encerrar conta</h2>
      </div>
      {open ? (
        <div class="space-y-3">
          <p class="text-xs text-muted-foreground">
            Encerramento solicitado em{" "}
            {new Date(open.requestedAt).toLocaleString("pt-BR")}. A
            operacionalização ocorre em até 30 dias, conforme os Termos de Uso.
            Você pode cancelar enquanto não for processado.
          </p>
          <button
            type="button"
            onClick={() => void cancel()}
            disabled={busy}
            class="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
          >
            Cancelar solicitação
          </button>
        </div>
      ) : !showForm ? (
        <div class="space-y-3">
          <p class="text-xs text-muted-foreground">
            Esta ação solicita o encerramento permanente da sua conta. Após a
            confirmação, processaremos em até 30 dias. Alguns dados podem ser
            retidos quando exigido por lei, e valores ou créditos não são
            reembolsáveis, salvo nas hipóteses legais.
          </p>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            class="rounded-md border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
          >
            Solicitar encerramento da conta
          </button>
        </div>
      ) : (
        <div class="space-y-3">
          <p class="text-xs text-muted-foreground">
            Para confirmar, digite <strong>{confirmPhrase}</strong> no campo
            abaixo.
          </p>
          <input
            type="text"
            value={typed}
            placeholder={confirmPhrase}
            onInput={(e) => setTyped((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <textarea
            value={reason}
            placeholder="Motivo (opcional)"
            rows={2}
            onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          {err && <p class="text-xs text-destructive">{err}</p>}
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || typed.trim().toUpperCase() !== confirmPhrase}
              class="rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              Confirmar encerramento
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setTyped("");
                setErr(null);
              }}
              class="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              Voltar
            </button>
          </div>
        </div>
      )}
      {msg && <p class="mt-2 text-xs text-muted-foreground">{msg}</p>}
    </section>
  );
}
